import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import api from '../../services/api';
import DataTable from '../../components/common/DataTable';

const PaymentList = ({ paymentType = 'receive' }) => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const isReceive = paymentType === 'receive';
  const title = isReceive ? 'PAYMENT RECEIVED LIST' : 'PAYMENT MADE LIST';
  const newLabel = isReceive ? 'Receive Payment' : 'Make Payment';

  useEffect(() => {
    loadPayments();
  }, [paymentType]);

  const loadPayments = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/payments?type=${paymentType}`);
      setPayments(res.data || []);
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e, row) => {
    e.stopPropagation();
    if (!window.confirm(`Delete payment ${row.payment_no}? Any applied amounts will be reversed.`)) return;
    try {
      await api.delete(`/payments/${row.id}`);
      toast.success('Payment deleted');
      loadPayments();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const formatCurrency = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  let filtered = payments;
  if (statusFilter) filtered = filtered.filter(p => p.status === statusFilter);
  if (searchQuery) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter(p =>
      (p.payment_no || '').toLowerCase().includes(q) ||
      (p.party || '').toLowerCase().includes(q) ||
      (p.check_no || '').toLowerCase().includes(q) ||
      (p.bank_name || '').toLowerCase().includes(q)
    );
  }

  const columns = [
    {
      key: 'payment_no', label: 'Payment No.', sortable: true,
      render: (v, row) => (
        <span style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 500 }}
          onClick={(e) => { e.stopPropagation(); navigate(`/payment/${row.id}`); }}>
          {v || '-'}
        </span>
      ),
    },
    { key: 'party', label: isReceive ? 'Received From' : 'Pay To', sortable: true },
    {
      key: 'customers_summary', label: isReceive ? 'Customer' : 'Vendor', sortable: true,
      render: (v) => {
        if (!v) return '-';
        const truncated = v.length > 25 ? v.substring(0, 25) + '...' : v;
        return <span title={v}>{truncated}</span>;
      },
    },
    {
      key: 'post_date', label: 'Post Date', sortable: true,
      render: (v) => v ? new Date(v).toLocaleDateString() : '-',
    },
    { key: 'type', label: 'Type', sortable: true },
    { key: 'check_no', label: 'Check No.', sortable: true },
    { key: 'bank_name', label: 'Bank', sortable: true },
    {
      key: 'total_amount', label: isReceive ? 'Received Amount' : 'Paid Amount', sortable: true,
      render: (v) => <span style={{ textAlign: 'right', display: 'block', fontWeight: 600 }}>{formatCurrency(v)}</span>,
    },
    {
      key: 'invoice_count', label: 'Invoices', sortable: true,
      render: (v) => <span style={{ textAlign: 'center', display: 'block' }}>{v || 0}</span>,
    },
    {
      key: 'deposit_date', label: isReceive ? 'Deposit Date' : 'Clear Date', sortable: true,
      render: (v) => v ? new Date(v).toLocaleDateString() : '-',
    },
    ...(isReceive ? [] : [
      {
        key: 'void_date', label: 'Void Date', sortable: true,
        render: (v) => v ? new Date(v).toLocaleDateString() : '-',
      },
    ]),
    { key: 'status', label: 'Status', sortable: true, type: 'status' },
    {
      key: '_delete', label: '', sortable: false,
      render: (v, row) => (
        <button onClick={(e) => handleDelete(e, row)} title="Delete"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center' }}
          onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
          onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}>
          <FiTrash2 size={14} />
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <h2 className="card-title" style={{ margin: 0 }}>{title}</h2>
            <span style={{ fontSize: '13px', color: '#64748b' }}>
              <strong>Total: </strong>
              <span style={{ color: '#2563eb', fontWeight: 700 }}>
                {isReceive ? 'Received' : 'Paid'} Amount: {formatCurrency(payments.reduce((s, p) => s + (Number(p.total_amount) || 0), 0))}
              </span>
            </span>
          </div>
          <button
            onClick={() => navigate(`/payment/new?mode=${paymentType}`)}
            style={{ padding: '8px 16px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FiPlus size={14} /> {newLabel}
          </button>
        </div>

        <div className="filter-bar">
          <div className="filter-group">
            <label className="filter-label">Status:</label>
            <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All</option>
              <option value="Draft">Draft</option>
              <option value="Applied">Applied</option>
              <option value="Void">Void</option>
            </select>
          </div>
          <div className="filter-group" style={{ flex: 1, marginLeft: 'auto' }}>
            <input
              type="text" className="filter-input"
              placeholder="Search by payment no, party, check no, bank..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <DataTable
          columns={columns}
          data={filtered}
          loading={loading}
          onRowClick={(row) => navigate(`/payment/${row.id}`)}
          pageSize={20}
        />
      </div>
    </div>
  );
};

export default PaymentList;
